/**
 * KraftStories — Klaviyo Email Signup Popup
 * (successor to mailchimp-signup-popup.js — same UX, submits to Klaviyo)
 *
 * Location detection (priority order):
 *  1. Auto-detected from page via window.__kraft_page_location_city (location pages)
 *  2. User-selected via city picker pills (non-location pages, e.g. homepage)
 *  3. Empty — visitor didn't pick and page has no location (still submits, no LOCATION property)
 *
 * Submits via Klaviyo Client Subscriptions API (fetch POST, CORS-enabled, no auth):
 *   POST https://a.klaviyo.com/client/subscriptions/?company_id={PUBLIC_KEY}
 * Adds the profile to the Email List → triggers the "Welcome Series - Classes" flow.
 * LOCATION is stored as a Klaviyo profile property (same values the old Mailchimp
 * merge field used: hawaii | new-york | san-francisco | san-jose | los-angeles | irvine | virtual).
 *
 * NOTE: the Email List uses double opt-in — Klaviyo sends a confirmation email and
 * the profile joins the list (and gets the welcome flow) after confirming.
 *
 * Cookie names are kept from the Mailchimp version on purpose, so visitors who
 * already dismissed/subscribed are not re-prompted after the migration.
 *
 * Re-show rules: the first close parks the popup as a pill for 5 days; the
 * second close (card or pill) hides both for 30 days. See CLOSE_LIMIT below.
 */
(function () {
  /* Session pageview counter — drives the A/B 'popup' delay rule. */
  try {
    var _pv = parseInt(sessionStorage.getItem('ks_ab_pageviews') || '0', 10) || 0;
    sessionStorage.setItem('ks_ab_pageviews', String(_pv + 1));
  } catch (e) {}

  'use strict';

  // Config injected by snippets/klaviyo-email-signup-popup.liquid
  var cfg = window.__kraftKlaviyoCfg;
  if (!cfg || !cfg.companyId || !cfg.listId) return;

  var COOKIE_CLOSED     = 'kf_mc_dismissed';
  var COOKIE_SIGNED     = 'kf_mc_subscribed';
  var COOKIE_MINIMIZED  = 'kf_mc_minimized';
  var COOKIE_CLOSES     = 'kf_mc_closes';   // running count of close actions
  var CLOSE_DAYS        = 5;
  var SIGNED_DAYS       = 365;
  var MINIMIZED_DAYS    = 5; // pill persists same duration as dismiss window

  /* ── Repeat-dismissal rule ──────────────────────────────────────────
   * Every close counts — minimising the card (X / Escape) and dismissing
   * the pill (✕) both increment kf_mc_closes. On the CLOSE_LIMIT-th close
   * the popup AND the pill go away for BACKOFF_DAYS: someone who has said
   * no twice has answered the question.
   * The counter cookie itself expires after COUNT_WINDOW_DAYS, so a single
   * close long ago doesn't count against a visitor forever.
   */
  var CLOSE_LIMIT       = 2;
  var BACKOFF_DAYS      = 30;
  var COUNT_WINDOW_DAYS = 30;
  var SHOW_DELAY        = 8000; // ms after page load
  var OVERLAY_ID        = 'klaviyo-signup';
  var OVERLAY_PRIORITY  = 10; // lowest — page-specific offers outrank the generic popup

  var KLAVIYO_ENDPOINT  = 'https://a.klaviyo.com/client/subscriptions/?company_id=' +
                          encodeURIComponent(cfg.companyId);
  var KLAVIYO_REVISION  = '2024-10-15';

  // ── Location mapping ──────────────────────────────────────────────
  // Maps window.__kraft_page_location_city → LOCATION profile property value.
  // Klaviyo segments/templates match these (same values as the old Mailchimp field).
  // Keys match normalized slugs from gtm-datalayer.liquid (metaobject-based)
  var cityToLocValue = {
    'hawaii':        'hawaii',
    'new-york':      'new-york',
    'san-francisco': 'san-francisco',
    'san-jose':      'san-jose',
    'los-angeles':   'los-angeles',
    'irvine':        'irvine',
    'virtual':       'virtual',
  };

  var cityToLabel = {
    'hawaii':        'Hawaii',
    'new-york':      'New York',
    'san-francisco': 'San Francisco',
    'san-jose':      'San Jose',
    'los-angeles':   'Los Angeles',
    'irvine':        'Irvine',
    'virtual':       'Virtual',
  };

  // ── Cookie helpers ────────────────────────────────────────────────
  function getCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    return m ? m.pop() : '';
  }

  function setCookie(name, days, value) {
    var exp = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + (value === undefined ? '1' : value) +
                      '; expires=' + exp + '; path=/; SameSite=Lax';
  }

  function clearCookie(name) {
    document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  // Number of times this visitor has closed the popup inside the count window
  function getCloseCount() {
    return parseInt(getCookie(COOKIE_CLOSES), 10) || 0;
  }

  // Records one close and returns the new total. The counter's own expiry is
  // refreshed on each close, so the window rolls forward from the last close.
  function registerClose() {
    var n = getCloseCount() + 1;
    setCookie(COOKIE_CLOSES, COUNT_WINDOW_DAYS, String(n));
    return n;
  }

  // ── Analytics helper ──────────────────────────────────────────────
  // Pushes to dataLayer (GTM) and mirrors to gtag via analytics-events.js.
  // Events: signup_popup_view | minimize | expand | dismiss | submit | success | error
  function track(eventName, params) {
    var payload = Object.assign({ popup_id: 'klaviyo_signup' }, params || {});
    if (window.__kraft_page_location_city) {
      payload.page_location_city = window.__kraft_page_location_city;
    }
    if (typeof window.__kraftGa4Mirror === 'function') {
      window.__kraftGa4Mirror(eventName, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
  }

  // ── Overlay manager shims (no-ops if overlay-manager.js is absent) ─
  function overlayRequest(opts) {
    if (!window.KraftOverlays) return true;
    return window.KraftOverlays.request(OVERLAY_ID, opts);
  }
  function overlayRelease() {
    if (window.KraftOverlays) window.KraftOverlays.release(OVERLAY_ID);
  }
  function overlayCancel() {
    if (window.KraftOverlays) window.KraftOverlays.cancel(OVERLAY_ID);
  }

  // ── Cart drawer guard ─────────────────────────────────────────────
  // Never surface the signup popup while the cart drawer is open — the
  // visitor is mid-purchase. We re-check until the drawer closes.
  function cartDrawerIsOpen() {
    var d = document.querySelector('.cart-drawer__dialog');
    return !!(d && d.hasAttribute('open'));
  }

  // ── Popup helpers ─────────────────────────────────────────────────
  // NOTE: the popup never blocks the page — no body scroll lock, and the
  // overlay has pointer-events:none (only the card/pill are interactive).
  function getPopup() { return document.getElementById('kf-mc-popup'); }

  // Full popup visible, pill hidden
  function showPopup() {
    var el = getPopup();
    if (!el) return;
    el.classList.remove('kf-mc-popup--minimized');
    el.classList.add('kf-mc-popup--visible');
  }

  // Hide card + pill outright and hold off for `days`
  function suppressFor(days) {
    var el = getPopup();
    if (el) {
      el.classList.remove('kf-mc-popup--visible');
      el.classList.remove('kf-mc-popup--minimized');
    }
    clearCookie(COOKIE_MINIMIZED);
    setCookie(COOKIE_CLOSED, days);
    overlayRelease();
  }

  // First close: shrink to pill + remember across pages.
  // Second close: the visitor has said no twice — back off for 30 days.
  function minimizePopup() {
    var el = getPopup();
    if (!el) return;
    if (registerClose() >= CLOSE_LIMIT) {
      suppressFor(BACKOFF_DAYS);
      track('signup_popup_suppressed', { close_count: getCloseCount(), days: BACKOFF_DAYS });
      return;
    }
    el.classList.add('kf-mc-popup--minimized');
    setCookie(COOKIE_MINIMIZED, MINIMIZED_DAYS);
    overlayRelease();
  }

  // Expand pill back to full card — always return to step 1
  function expandPopup() {
    var el = getPopup();
    if (!el) return;
    // Reset to step 1 in case user had reached step 2 before minimizing
    var s1 = document.getElementById('kf-mc-step-email');
    var s2 = document.getElementById('kf-mc-step-loc');
    if (s1) s1.style.display = '';
    if (s2) s2.classList.remove('kf-mc-step--visible');
    el.classList.remove('kf-mc-popup--minimized');
    clearCookie(COOKIE_MINIMIZED);
    // User-initiated re-open: take the slot if it happens to be free.
    overlayRequest({ queue: false });
  }

  // Final dismiss: fully hide. 5 days on the first close, 30 on the second.
  function dismissPopup() {
    var el = getPopup();
    if (!el) return;
    var closes = registerClose();
    suppressFor(closes >= CLOSE_LIMIT ? BACKOFF_DAYS : CLOSE_DAYS);
  }

  // ── State helpers ─────────────────────────────────────────────────
  function showSuccess(popup) {
    var formArea    = popup.querySelector('.kf-mc-popup__form-area');
    var successArea = popup.querySelector('.kf-mc-popup__success');
    if (formArea)    formArea.style.display    = 'none';
    if (successArea) successArea.style.display = 'block';
  }

  function showError(popup, msg) {
    var errEl = popup.querySelector('.kf-mc-popup__error');
    if (errEl) { errEl.textContent = msg; errEl.style.display = ''; }
  }

  /* ── Long-form articles ───────────────────────────────────────────
   * The popup is suppressed on blog articles entirely.
   *
   * On the pop-culture trivia post last week: 142 visitors, 105 of them saw
   * this popup and 93 minimised it — an 89% rejection rate, the worst on the
   * site, from the most patient audience on the site (93 of 142 read past a
   * minute). Those readers are worth a workshop link, not an interruption;
   * snippets/article-cta-bar.liquid gives them one instead.
   *
   * This is deliberately NOT part of the popup-delay A/B test. It removes the
   * same population from BOTH arms, so the running experiment stays balanced —
   * it loses some volume, and gains a cleaner question, since it no longer
   * averages long-read behaviour together with booking-page behaviour.
   *
   * Path-based rather than template-based because this file is a plain asset
   * with no access to Liquid. Shopify article URLs are always three segments:
   * /blogs/<blog-handle>/<article-handle>. The two-segment form is the blog
   * index, which is a listing page and keeps the popup.
   */
  function isLongFormArticle() {
    try {
      var parts = window.location.pathname.split('/').filter(Boolean);
      if (parts[0] === 'blogs' && parts.length >= 3) return true;
      /* Localised storefronts prefix a locale: /ja/blogs/<blog>/<article> */
      if (parts.length >= 4 && parts[1] === 'blogs' && parts[0].length <= 5) return true;
      return false;
    } catch (e) { return false; }
  }

  // ── Main init ─────────────────────────────────────────────────────
  function init() {
    if (window.KS_POPUP_SUPPRESS_ARTICLES !== false && isLongFormArticle()) {
      overlayCancel();
      return;
    }

    // Don't show if visitor already dismissed or subscribed
    if (getCookie(COOKIE_CLOSED) || getCookie(COOKIE_SIGNED)) { overlayCancel(); return; }

    var wasMinimized = !!getCookie(COOKIE_MINIMIZED);

    var popup = getPopup();
    if (!popup) return;

    // Detect current page location
    var city     = (typeof window.__kraft_page_location_city === 'string')
                   ? window.__kraft_page_location_city.trim()
                   : '';
    var label    = cityToLabel[city];
    var locValue = cityToLocValue[city];

    // Tracks city chosen via step-2 picker pills
    var userPickedCity = '';

    // Auto-fill hidden LOCATION field (kept for parity/debugging)
    var locationInput = document.getElementById('kf-mc-location');
    if (locationInput) locationInput.value = locValue || '';

    // Personalise step-1 kicker if location was auto-detected from the page
    // (headline stays static; the kicker carries the location, e.g. "KraftStories Hawaii")
    if (label) {
      var kickerEl = document.querySelector('#kf-mc-step-email .kf-mc-popup__kicker');
      if (kickerEl) kickerEl.textContent = 'KraftStories ' + label;
    }

    // ── Step references ───────────────────────────────────────────────────────
    var stepEmail = document.getElementById('kf-mc-step-email');
    var stepLoc   = document.getElementById('kf-mc-step-loc');

    // showLocationStep: swap step 1 → step 2
    function showLocationStep() {
      if (stepEmail) stepEmail.style.display = 'none';
      if (stepLoc)   stepLoc.classList.add('kf-mc-step--visible');
    }

    // If previously minimized, show as pill immediately; otherwise show full popup after delay.
    // The pill is a small parked chip, not a full overlay, so it bypasses the manager.
    if (wasMinimized) {
      showPopup();
      minimizePopup();
      track('signup_popup_view', { display: 'pill' });
    } else {
      /* ── A/B 'popup': does the interruption cost bookings? ────────────
       * 1,621 visitors saw this last week and 1,079 minimised it.
       * A: unchanged (8s after load).
       * B: hold until the visitor shows intent — 45s on the first page of the
       *    session, normal timing from the second page onward.
       * Judge this on downstream view_item / add_to_cart, NOT on signups:
       * at a 1.2% submit rate a +20% lift would take ~314 days to detect.
       * ---------------------------------------------------------------- */
      var abDelay = SHOW_DELAY;
      try {
        var abPopup = window.KS_AB && window.KS_AB.popup;
        if (abPopup && abPopup.mode === 'test' && abPopup.variant === 'b') {
          var seen = 0;
          try { seen = parseInt(sessionStorage.getItem('ks_ab_pageviews') || '0', 10) || 0; } catch (e) {}
          abDelay = seen >= 2 ? SHOW_DELAY : 45000;
        }
      } catch (e) {}

      setTimeout(function () {
        // Only one floating overlay at a time — ask the manager first.
        var reveal = function () {
          showPopup();
          track('signup_popup_view', { display: 'card', trigger: 'auto' });
          /* Exposure fires here, not at page load: only visitors who actually
           * reach the point of seeing it belong in the denominator. Safe to
           * call now — the deferred engine has long since loaded. */
          if (typeof window.KS_AB_track === 'function') {
            window.KS_AB_track('popup', 'view', { display: 'card' });
            /* 'offer' (incentive copy test) shares the popup's exposure:
             * a visitor is in that experiment's denominator iff the popup
             * was actually shown to them. Guarded so it is a no-op unless
             * the offer experiment is registered and active. */
            if (window.KS_AB && window.KS_AB.offer) {
              window.KS_AB_track('offer', 'view', { display: 'card' });
            }
          }
        };
        var attemptReveal = function () {
          if (cartDrawerIsOpen()) { setTimeout(attemptReveal, 2500); return; }
          if (overlayRequest({ priority: OVERLAY_PRIORITY, show: reveal })) reveal();
        };
        attemptReveal();
      }, abDelay);
    }

    // ── Close triggers ─────────────────────────────────────────────
    // Card X button → minimize to pill (first close)
    popup.querySelectorAll('[data-mc-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        minimizePopup();
        track('signup_popup_minimize', { trigger: 'close_button' });
        if (typeof window.KS_AB_track === 'function') {
          window.KS_AB_track('popup', 'minimize', { trigger: 'close_button' });
          if (window.KS_AB && window.KS_AB.offer) window.KS_AB_track('offer', 'minimize', { trigger: 'close_button' });
        }
      });
    });

    // Escape → minimize (only when the full card is actually visible)
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var el = getPopup();
      if (!el || !el.classList.contains('kf-mc-popup--visible') ||
          el.classList.contains('kf-mc-popup--minimized')) return;
      minimizePopup();
      track('signup_popup_minimize', { trigger: 'escape_key' });
    });

    // ── Mini pill triggers ─────────────────────────────────────────
    // Click pill body → re-open full popup
    var miniExpand = popup.querySelector('.kf-mc-popup__mini-expand');
    if (miniExpand) {
      miniExpand.addEventListener('click', function () {
        expandPopup();
        track('signup_popup_expand', {});
        track('signup_popup_view', { display: 'card', trigger: 'expand' });
      });
    }

    // Click pill ✕ (or success "Got it") → fully dismiss + 5-day cookie
    popup.querySelectorAll('[data-mc-dismiss]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        dismissPopup();
        track('signup_popup_dismiss', {
          close_count: getCloseCount(),
          suppressed_days: getCloseCount() >= CLOSE_LIMIT ? BACKOFF_DAYS : CLOSE_DAYS
        });
      });
    });

    // ── Klaviyo submit helper ──────────────────────────────────────────────────
    var form = popup.querySelector('.kf-mc-popup__form');
    if (!form) return;

    function submitToKlaviyo(resolvedLocation) {
      if (locationInput) locationInput.value = resolvedLocation || '';

      track('signup_popup_submit', {
        popup_location: resolvedLocation || '(none)',
      });

      var emailInput = form.querySelector('input[name="EMAIL"]');
      var emailVal   = emailInput ? emailInput.value.trim() : '';

      var submitBtn = form.querySelector('.kf-mc-popup__submit');
      var doneBtn   = document.getElementById('kf-mc-loc-done');

      // Disable both buttons so double-submit is impossible
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }
      if (doneBtn)   { doneBtn.disabled = true; doneBtn.textContent = 'Sending…'; }

      function resetSubmit(errMsg) {
        // Re-show step 1 with the error
        if (stepLoc)   stepLoc.classList.remove('kf-mc-step--visible');
        if (stepEmail) stepEmail.style.display = '';
        if (errMsg) showError(popup, errMsg);
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Count me in'; }
        if (doneBtn)   { doneBtn.disabled = false; doneBtn.textContent = 'Done'; }
      }

      var profileAttributes = { email: emailVal };
      if (resolvedLocation) {
        profileAttributes.properties = { LOCATION: resolvedLocation };
      }

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Website signup popup',
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes
              }
            }
          },
          relationships: {
            list: { data: { type: 'list', id: cfg.listId } }
          }
        }
      };

      var controller = ('AbortController' in window) ? new AbortController() : null;
      var abortTimer = controller ? setTimeout(function () { controller.abort(); }, 8000) : null;

      fetch(KLAVIYO_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          'revision': KLAVIYO_REVISION
        },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) {
          if (abortTimer) clearTimeout(abortTimer);
          if (res.ok) { // 202 Accepted
            showSuccess(popup);
            setCookie(COOKIE_SIGNED, SIGNED_DAYS);
            track('signup_popup_success', {
              popup_location: resolvedLocation || '(none)',
            });
            return;
          }
          return res.json().then(function (body) {
            var msg = (body && body.errors && body.errors[0] && body.errors[0].detail)
              ? body.errors[0].detail
              : 'Please try again.';
            track('signup_popup_error', { error_type: 'api', error_message: msg });
            resetSubmit(msg);
          }, function () {
            track('signup_popup_error', { error_type: 'api', error_message: 'Unparseable response' });
            resetSubmit('Please try again.');
          });
        })
        .catch(function () {
          if (abortTimer) clearTimeout(abortTimer);
          track('signup_popup_error', { error_type: 'network' });
          resetSubmit('Connection error — please try again.');
        });
    }

    // ── Step 1: email validation → decide whether to show location step ───────
    // Clear error on input
    var emailField = form.querySelector('input[name="EMAIL"]');
    if (emailField) {
      emailField.addEventListener('input', function () {
        var errEl = popup.querySelector('.kf-mc-popup__error');
        if (errEl) { errEl.textContent = ''; errEl.style.display = 'none'; }
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var emailInput = form.querySelector('input[name="EMAIL"]');
      var emailVal   = emailInput ? emailInput.value.trim() : '';
      if (!emailVal) { showError(popup, 'Please enter your email address.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailVal)) {
        showError(popup, 'Please enter a valid email address.'); return;
      }

      // Re-check page location at submit time (GTM may have fired late)
      var latestCity  = (typeof window.__kraft_page_location_city === 'string')
                        ? window.__kraft_page_location_city.trim() : '';
      var resolvedLoc = cityToLocValue[latestCity] || '';

      if (resolvedLoc) {
        // Location already known from the page — skip step 2, submit immediately
        submitToKlaviyo(resolvedLoc);
      } else {
        // No page location detected — show step 2 for optional city pick
        showLocationStep();
      }
    });

    // ── Step 2: location pill selection ───────────────────────────────────────
    if (stepLoc) {
      stepLoc.querySelectorAll('.kf-mc-popup__loc-pill').forEach(function (pill) {
        pill.addEventListener('click', function () {
          var isSame = pill.classList.contains('is-selected');
          stepLoc.querySelectorAll('.kf-mc-popup__loc-pill').forEach(function (p) {
            p.classList.remove('is-selected');
          });
          if (!isSame) {
            pill.classList.add('is-selected');
            userPickedCity = pill.dataset.loc || '';
          } else {
            userPickedCity = '';
          }
        });
      });

      // "Done" — submit with whatever city is selected (may be empty if none picked)
      var doneBtn = document.getElementById('kf-mc-loc-done');
      if (doneBtn) {
        doneBtn.addEventListener('click', function () {
          submitToKlaviyo(cityToLocValue[userPickedCity] || '');
        });
      }

      // "Skip" — submit without location
      var skipBtn = document.getElementById('kf-mc-loc-skip');
      if (skipBtn) {
        skipBtn.addEventListener('click', function () {
          submitToKlaviyo('');
        });
      }
    }
  }

  // Init after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
